/**
 * Склад лівої навігації (UX-02) і стартовий маршрут (UX-01) з метаданих `/meta` — чисті функції
 * без React: що показати, вирішують права людини й метадані, а не код оболонки.
 * - Пункти реєстру — `/meta.nav`: меню, яке оголосив пакет компанії; права вже відфільтрував сервер.
 * - Пункти модулів — слот `nav` вебманіфестів, лише з правом (`navItems`, platform/modules.ts).
 * - Пункти реєстру й модулів — один список за `order`; перші `NAV_MAX_ITEMS` — у меню, решта — у
 *   «Ще» (П-05: до 7 пунктів).
 * - Стартова сторінка — `start` з `/meta` (сервер обчислює ланцюжок вибору, views.md, розд. 5).
 *   Якщо стартове подання вже є пунктом модуля, окремого пункту не додаємо.
 *   Немає стартового подання — відкривається перший доступний пункт (UX-01).
 * - «Адміністрування» — лише коли людині доступна хоч одна сторінка (П-05: недоступного не видно).
 */
import type { AppMeta } from '@clientline/contracts';
import type { IconName } from '../ui/Icon.tsx';

export interface NavEntry {
  id: string;
  to: string;
  label: string;
  icon: IconName;
  /** Активний лише на точній адресі; інакше — і на вкладених (`/admin/...`). */
  exact: boolean;
  /**
   * Пункт сутності з реєстру: активний на будь-якому її поданні (`/l/<сутність>.*`) і картці
   * (`/r/<сутність>/*`), а не лише на адресі пункту.
   */
  entity?: string;
}

export interface ShellNav {
  /** Основні розділи — угорі рейки й у нижній навігації телефона; не більше `NAV_MAX_ITEMS`. */
  items: NavEntry[];
  /** Решта розділів — у пункті «Ще» (UX-02); порожньо — пункту немає. */
  more?: NavEntry[];
  /** «Адміністрування» — унизу рейки, лише ПК. */
  admin: NavEntry | null;
  /** Куди веде «/» (логотип, G → H); `null` — доступних розділів немає. */
  start: string | null;
  /** Переходи лише для палітри `Ctrl+K` (не пункти рейки): «Довідники» всередині «Налаштувань». */
  extra?: NavEntry[];
}

/** Пункт слоту `nav` після фільтра прав (форма `navItems`). */
export interface ModuleNavItem {
  id: string;
  route: string;
  text: string;
  icon?: string | undefined;
  /** Місце в одному списку з пунктами реєстру; немає — після них. */
  order?: number | undefined;
}

/** Пунктів у меню, решта — у «Ще» (П-05, UX-02). */
export const NAV_MAX_ITEMS = 7;

export const ADMIN_PATH = '/admin';
/** «Мій профіль» (PRF-01): з меню користувача й палітри, не пункт навігації. */
export const PROFILE_PATH = '/profile';
/** Пункт «Пошта» вебманіфесту модуля: лічильник непрочитаних і `G` → `M`. */
export const MAIL_NAV_ID = 'mail.cabinet';
/** Кошик людини (DAT-05; trash.html, кадр В2): `?entity=<ключ>` — вкладка сутності. */
export const TRASH_PATH = '/trash';
/** Черга можливих дублів (DAT-07): `?entity=<ключ>` — вкладка сутності; злиття — `/merge/<сутність>`. */
export const DUPLICATES_PATH = '/duplicates';

const ICONS = new Set<string>([
  'home',
  'list',
  'task',
  'briefcase',
  'building',
  'user',
  'users',
  'calendar',
  'mail',
  'phone',
  'note',
  'file',
  'history',
  'inbox',
  'kanban',
]);

/** Іконка з маніфесту — лише з набору платформи; невідома назва — нейтральний список. */
function iconOf(name: string | undefined): IconName {
  return (name && ICONS.has(name) ? name : 'list') as IconName;
}

export function viewPath(view: string): string {
  return `/l/${view}`;
}

/**
 * Сутність розділу за адресою: подання списком `/l/<сутність>.<ключ>`, дошкою `/b/<сутність>.<ключ>`
 * чи картка `/r/<сутність>/…`. Одне джерело для навігації й панелі подання (аудит якості № 16).
 */
export function entityOfPath(pathname: string): string | null {
  const list = /^\/[lb]\/([a-z][a-z0-9_]*)\.[^/]+$/.exec(pathname);
  if (list) return list[1] ?? null;
  const record = /^\/r\/([a-z][a-z0-9_]*)\//.exec(pathname);
  return record ? (record[1] ?? null) : null;
}

/**
 * Чи пункт навігації — поточний розділ: точна адреса, вкладена (для не-`exact`) або будь-яке подання
 * чи картка його сутності (UX-02). Одне правило для пунктів рейки, нижньої навігації й меню «Ще».
 */
export function isCurrentNav(item: NavEntry, pathname: string): boolean {
  if (item.entity !== undefined && item.entity === entityOfPath(pathname)) return true;
  const trim = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);
  const path = trim(pathname);
  const to = trim(item.to);
  if (path === to) return true;
  return !item.exact && (to === '/' || path.startsWith(`${to}/`));
}

/** Пункти реєстру й модулів одним списком за `order` (стабільно: рівні — у порядку джерела). */
function orderedItems(meta: AppMeta, moduleItems: readonly ModuleNavItem[]): NavEntry[] {
  const registry = (meta.nav ?? []).map((item) => ({
    order: item.order,
    entry: {
      id: item.id,
      to: viewPath(item.view),
      // Мова інтерфейсу поки одна — українська (як на екранах платформи S9).
      label: item.label.uk,
      icon: iconOf(item.icon),
      exact: true,
      entity: item.entity,
    } satisfies NavEntry,
  }));
  const modules = moduleItems.map((item) => ({
    order: item.order ?? Number.POSITIVE_INFINITY,
    entry: {
      id: item.id,
      to: item.route,
      label: item.text,
      icon: iconOf(item.icon),
      exact: true,
    } satisfies NavEntry,
  }));
  return [...registry, ...modules].sort((a, b) => a.order - b.order).map((item) => item.entry);
}

export function buildNav(
  meta: AppMeta,
  moduleItems: readonly ModuleNavItem[],
  adminLabel: string | null,
  adminTo: string = ADMIN_PATH,
  extra: NavEntry[] = [],
): ShellNav {
  const all = orderedItems(meta, moduleItems);
  const startView = meta.start.view ? meta.views.find((v) => v.key === meta.start.view) : undefined;
  if (startView) {
    const to = viewPath(startView.key);
    if (!all.some((item) => item.to === to)) {
      // Мова інтерфейсу поки одна — українська (як на екранах платформи S9).
      all.unshift({ id: 'start', to, label: startView.shortLabel.uk, icon: 'home', exact: true });
    }
  }
  const items = all.slice(0, NAV_MAX_ITEMS);
  const more = all.slice(NAV_MAX_ITEMS);
  const admin: NavEntry | null =
    adminLabel === null
      ? null
      : {
          id: 'admin',
          to: adminTo,
          label: adminLabel,
          icon: adminTo === ADMIN_PATH ? 'settings' : 'list',
          exact: false,
        };
  const start = startView ? viewPath(startView.key) : (items[0]?.to ?? admin?.to ?? null);
  return { items, ...(more.length > 0 ? { more } : {}), admin, start, extra };
}

/** Навігація без `/meta` (вантажиться чи сесія скінчилась): «На головну» і «Увійти». */
export function guestNav(homeLabel: string, signInLabel: string): ShellNav {
  return {
    items: [
      { id: 'home', to: '/', label: homeLabel, icon: 'home', exact: true },
      { id: 'login', to: '/login', label: signInLabel, icon: 'lock', exact: true },
    ],
    admin: null,
    start: '/',
  };
}

/**
 * Особиста стартова сторінка (PRF-06): шлях із профілю людини (кеш `prefs.startPage`), якщо він є
 * серед її пунктів навігації — тобто доступний за правами. Інакше лишається стартова з `/meta`, а
 * `rejected` каже екрану, що вибір людини не відкрито (тост із причиною).
 */
export function withPersonalStart(
  nav: ShellNav,
  startPage: string,
): { nav: ShellNav; rejected: boolean } {
  if (!startPage || nav.start === null || startPage === nav.start) return { nav, rejected: false };
  const allowed = [...nav.items, ...(nav.more ?? []), ...(nav.admin ? [nav.admin] : [])].some(
    (e) => e.to === startPage,
  );
  return allowed ? { nav: { ...nav, start: startPage }, rejected: false } : { nav, rejected: true };
}
